/**
 * The route registry — one eager table the whole app is derived from:
 * both route trees (`/` live, `/test` mock), the sidebar, breadcrumbs, the
 * top bar's controls and whether a page draws a filters bar.
 *
 * Replaces the prototype's hash router, which derived routes from the
 * sidebar's *story fixtures*.
 *
 * `live: false` keeps a page out of the `/` tree (and its sidebar row hidden)
 * until its widgets have real API loaders. Flip it only when
 * `pnpm test` (live-route gate) passes — see docs/taking-a-page-live.md.
 */
import type { IconName } from '@deepdive-ui';
import type { ComponentType } from 'react';

import type { FiltersType, WidgetId } from '@/data-sources/contract';

/** The top bar's shape per page (the prototype's `HeaderView.barFor`). */
export type TopBarKind =
  | 'module' // date range + comparison + Export
  | 'reports-list' // Create a report
  | 'report-detail' // date range + comparison, no CTA
  | 'deepdive-home' // Chat history
  | 'deepdive-history' // nothing
  | 'deepdive-chat' // Chat history + New chat
  | 'settings' // Cancel + Save changes
  | 'quick-search' // breadcrumb only
  | 'influencer-detail'; // date range only

export type NavSectionId = 'top' | 'monitor' | 'intelligence' | 'footer';

export type PageModule = { default: ComponentType };

export type AppRoute = {
  /** Stable id; also the sidebar item that lights up. */
  id: string;
  /** Path relative to the tree base, e.g. '' (index), 'mentions', 'custom-reports/:reportId'. */
  path: string;
  page: () => Promise<PageModule>;
  /** The sidebar row this route belongs under (defaults to the module id). */
  navItem: string;
  filters: FiltersType | null;
  topBar: TopBarKind;
  /**
   * Trailing breadcrumbs beyond "Section > Module" — a dynamic crumb (report
   * name, chat title…) is resolved by `useCrumbLabel` in the top bar.
   */
  crumbs?: readonly CrumbSpec[];
  /**
   * The analytics widgets this page renders. Required before `live: true`:
   * the live-route test fails if any of them has no live loader yet.
   */
  widgets?: readonly WidgetId[];
  /** A floating actions row pinned to the window (Shell `dock`). */
  dock?: 'report-actions';
  /** Mounted under `/`? */
  live: boolean;
};

export type CrumbSpec =
  | { kind: 'static'; label: string }
  | {
      kind: 'param';
      param: string;
      resolve: 'report' | 'chat' | 'influencer' | 'industry' | 'quick-search';
    };

/** A sidebar row. Order here is sidebar order (Reef `sidebar.fixtures.ts`). */
export type NavItem = {
  id: string;
  label: string;
  icon: IconName;
  section: NavSectionId;
  /** Canonical path (no base), e.g. '/mentions'. */
  href: string;
  badge?: { type: 'info' | 'positive'; label: string };
};

export const NAV_SECTIONS: readonly { id: Exclude<NavSectionId, 'footer'>; label?: string }[] = [
  { id: 'top' },
  { id: 'monitor', label: 'Monitor' },
  { id: 'intelligence', label: 'Intelligence' },
];

export const NAV_ITEMS: readonly NavItem[] = [
  { id: 'overview', label: 'Overview', icon: 'overview', section: 'top', href: '/' },
  {
    id: 'custom-reports',
    label: 'Custom reports',
    icon: 'customReport',
    section: 'top',
    href: '/custom-reports',
  },
  {
    id: 'deepdive-ai',
    label: 'DeepDive AI',
    icon: 'deepdiveAi',
    section: 'top',
    href: '/deepdive-ai',
    badge: { type: 'info', label: 'Beta' },
  },
  {
    id: 'quick-search',
    label: 'Quick search',
    icon: 'quickSearch',
    section: 'top',
    href: '/quick-search',
  },
  { id: 'mentions', label: 'Mentions', icon: 'socialFeed', section: 'monitor', href: '/mentions' },
  {
    id: 'influencers',
    label: 'Influencers',
    icon: 'influencers',
    section: 'monitor',
    href: '/influencers',
  },
  { id: 'media', label: 'Media', icon: 'media', section: 'monitor', href: '/media' },
  {
    id: 'brand-health',
    label: 'Brand health',
    icon: 'brandHealth',
    section: 'intelligence',
    href: '/brand-health',
  },
  {
    id: 'community',
    label: 'Community',
    icon: 'audience',
    section: 'intelligence',
    href: '/community',
  },
  {
    id: 'competitors',
    label: 'Competitors',
    icon: 'competitors',
    section: 'intelligence',
    href: '/competitors',
  },
  { id: 'trends', label: 'Trends', icon: 'trends', section: 'intelligence', href: '/trends' },
  { id: 'settings', label: 'Settings', icon: 'settings', section: 'footer', href: '/settings' },
];

const page = (load: () => Promise<PageModule>) => load;

export const APP_ROUTES: readonly AppRoute[] = [
  {
    id: 'overview',
    path: '',
    navItem: 'overview',
    page: page(() => import('@/pages/Overview')),
    filters: 'default',
    topBar: 'module',
    widgets: [
      'total-mentions',
      'share-of-voice-figure',
      'total-unique-audience',
      'negative-conversation',
      'nps',
      'activity-by-day-and-hour',
      'share-of-voice',
      'complaints-by-division',
      'community-topics',
      'mention-volume-over-time',
      'competitor-sentiment-comparison',
      'trending-keywords',
      'topic-sentiment-split',
    ],
    live: false,
  },
  {
    id: 'custom-reports',
    path: 'custom-reports',
    navItem: 'custom-reports',
    page: page(() => import('@/pages/CustomReports')),
    filters: null,
    topBar: 'reports-list',
    widgets: [],
    live: false,
  },
  {
    id: 'custom-report',
    path: 'custom-reports/:reportId',
    navItem: 'custom-reports',
    page: page(() => import('@/pages/ReportDetail')),
    filters: 'competitors',
    topBar: 'report-detail',
    crumbs: [{ kind: 'param', param: 'reportId', resolve: 'report' }],
    dock: 'report-actions',
    widgets: [],
    live: false,
  },
  {
    id: 'deepdive-ai',
    path: 'deepdive-ai',
    navItem: 'deepdive-ai',
    page: page(() => import('@/pages/DeepDive')),
    filters: null,
    topBar: 'deepdive-home',
    widgets: [],
    live: false,
  },
  {
    id: 'deepdive-history',
    path: 'deepdive-ai/history',
    navItem: 'deepdive-ai',
    page: page(() => import('@/pages/ChatHistory')),
    filters: null,
    topBar: 'deepdive-history',
    crumbs: [{ kind: 'static', label: 'Chat history' }],
    widgets: [],
    live: false,
  },
  {
    id: 'deepdive-chat',
    path: 'deepdive-ai/:chatId',
    navItem: 'deepdive-ai',
    page: page(() => import('@/pages/DeepDiveChat')),
    filters: null,
    topBar: 'deepdive-chat',
    crumbs: [{ kind: 'param', param: 'chatId', resolve: 'chat' }],
    widgets: [],
    live: false,
  },
  {
    id: 'quick-search',
    path: 'quick-search',
    navItem: 'quick-search',
    page: page(() => import('@/pages/QuickSearch')),
    filters: null,
    topBar: 'quick-search',
    widgets: [],
    live: false,
  },
  {
    id: 'quick-search-entry',
    path: 'quick-search/:historyEntryId',
    navItem: 'quick-search',
    page: page(() => import('@/pages/QuickSearchEntry')),
    filters: null,
    topBar: 'quick-search',
    crumbs: [{ kind: 'param', param: 'historyEntryId', resolve: 'quick-search' }],
    widgets: [],
    live: false,
  },
  {
    id: 'mentions',
    path: 'mentions',
    navItem: 'mentions',
    page: page(() => import('@/pages/Mentions')),
    filters: 'default',
    topBar: 'module',
    widgets: ['mention-volume-over-time', 'mentions-by-platform', 'intent-based-mentions'],
    live: false,
  },
  {
    id: 'influencers',
    path: 'influencers',
    navItem: 'influencers',
    page: page(() => import('@/pages/Influencers')),
    filters: null,
    topBar: 'module',
    widgets: ['influencer-analytics'],
    live: false,
  },
  {
    id: 'influencer',
    path: 'influencers/:influencerId',
    navItem: 'influencers',
    page: page(() => import('@/pages/InfluencerDetail')),
    filters: null,
    topBar: 'influencer-detail',
    crumbs: [{ kind: 'param', param: 'influencerId', resolve: 'influencer' }],
    widgets: ['influencer-profile', 'top-performing-content'],
    live: false,
  },
  {
    id: 'influencer-industry',
    path: 'influencers/:influencerId/:industry',
    navItem: 'influencers',
    page: page(() => import('@/pages/IndustryDetail')),
    filters: null,
    topBar: 'influencer-detail',
    crumbs: [
      { kind: 'param', param: 'influencerId', resolve: 'influencer' },
      { kind: 'param', param: 'industry', resolve: 'industry' },
    ],
    widgets: ['influencer-industry', 'top-performing-content'],
    live: false,
  },
  {
    id: 'media',
    path: 'media',
    navItem: 'media',
    page: page(() => import('@/pages/Media')),
    filters: 'default',
    topBar: 'module',
    widgets: [
      'media-total-mentions',
      'media-positive-mentions',
      'media-negative-mentions',
      'top-media-outlets',
      'media-sentiment-over-time',
    ],
    live: false,
  },
  {
    id: 'brand-health',
    path: 'brand-health',
    navItem: 'brand-health',
    page: page(() => import('@/pages/BrandHealth')),
    filters: 'default',
    topBar: 'module',
    widgets: [
      'nps',
      'seeded-vs-organic',
      'sentiment-over-time',
      'engagement-volume',
      'engagement-by-type',
      'topic-sentiment-split',
      'engagement-growth',
    ],
    live: false,
  },
  {
    id: 'community',
    path: 'community',
    navItem: 'community',
    page: page(() => import('@/pages/Community')),
    filters: 'default',
    topBar: 'module',
    widgets: [
      'top-communities-by-mention',
      'activity-by-day-and-hour',
      'top-voices-table',
      'community-activity-timeline',
      'audience-age-and-gender',
      'complaints-by-division',
      'top-individual-voices',
      'top-communities-by-sentiment',
      'top-communities-table',
      'community-topics',
    ],
    live: false,
  },
  {
    id: 'competitors',
    path: 'competitors',
    navItem: 'competitors',
    page: page(() => import('@/pages/Competitors')),
    filters: 'competitors',
    topBar: 'module',
    widgets: [
      'share-of-voice',
      'share-of-positive',
      'share-of-voice-over-time',
      'competitive-page-performance',
      'mention-volume-vs-competitors',
      'competitor-sentiment-comparison',
      'top-performing-content',
    ],
    live: false,
  },
  {
    id: 'trends',
    path: 'trends',
    navItem: 'trends',
    page: page(() => import('@/pages/Trends')),
    filters: 'default',
    topBar: 'module',
    widgets: [
      'top-hashtags',
      'keyword-sentiment',
      'engagement-spikes',
      'trending-topics',
      'trending-keywords',
      'keyword-mentions-frequency',
    ],
    live: false,
  },
  {
    id: 'settings',
    path: 'settings/:tab',
    navItem: 'settings',
    page: page(() => import('@/pages/Settings')),
    filters: null,
    topBar: 'settings',
    widgets: [],
    live: false,
  },
];

export const routeById = new Map(APP_ROUTES.map((route) => [route.id, route]));
export const navItemById = new Map(NAV_ITEMS.map((item) => [item.id, item]));

/** Section label for breadcrumbs ("Monitor", "Intelligence"), if the nav item has one. */
export function sectionLabelOf(navItemId: string): string | undefined {
  const item = navItemById.get(navItemId);
  return NAV_SECTIONS.find((section) => section.id === item?.section)?.label;
}
